import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useForm } from "react-hook-form";
import { Link as RouterLink, useParams } from "react-router-dom";
import Loading from "components/Loading";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import VisitStartElementGovernanceSection from "components/fde/VisitStartElementGovernanceSection";
import { useAuth } from "hooks/useAuth";
import { useFdeRole } from "hooks/useFdeRole";
import { listTciRegistry } from "services/fde/fdeRegistryService";
import {
  AUTONOMY_POSTURES,
  CMR_USAGE_MODES,
  GOVERNANCE_STATUS_LABELS,
  REVIEW_CADENCES,
  formatCmrUsageModeLabel,
  getTciGovernanceProfile,
  saveTciGovernanceProfile,
} from "services/fde/tciGovernanceService";
import { VISIT_START_TCI_ID } from "services/fde/visitStartElementsCatalog";
import PromptGraphEditor from "./PromptGraphEditor";
import TciSharingControl from "components/fde/TciSharingControl";
import { db } from "services/firebase";
import { useVisitStartFacilityRoles } from "pages/visitStart/useVisitStartFacilityRoles";

const TciGovernanceView = () => {
  const { tciId } = useParams();
  const { userData, organizationId, isGlobalAdmin } = useAuth();
  const { isFdeDc } = useFdeRole();
  const { isAdminGovernor } = useVisitStartFacilityRoles();
  const institutionId =
    userData?.activeInstitutionId ||
    userData?.institutionId ||
    organizationId ||
    null;
  const isVisitStart = tciId === VISIT_START_TCI_ID;

  const [profile, setProfile] = useState(null);
  const [tciRecord, setTciRecord] = useState(null);
  const [tciName, setTciName] = useState(tciId);
  const [loading, setLoading] = useState(true);
  const [savingNudge, setSavingNudge] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [savingGraph, setSavingGraph] = useState(false);

  const canEditNudge =
    Boolean(isGlobalAdmin) ||
    (userData?.role === "admin" && Boolean(institutionId)) ||
    isFdeDc;

  const visitStartAllowedCategories = useMemo(() => {
    if (Boolean(isGlobalAdmin) || userData?.role === "admin") {
      return ["administrative", "clinical"];
    }
    const cats = [];
    if (isFdeDc) cats.push("clinical");
    if (isAdminGovernor) cats.push("administrative");
    return cats;
  }, [isAdminGovernor, isFdeDc, isGlobalAdmin, userData?.role]);

  const canEditVisitStartElements = visitStartAllowedCategories.length > 0;

  const canEditGraph =
    Boolean(isGlobalAdmin) ||
    (userData?.role === "admin" && Boolean(institutionId)) ||
    isFdeDc;

  const { register, handleSubmit, reset } = useForm({
    defaultValues: { candidateNudgeThreshold: 2 },
  });

  const loadProfile = useCallback(async () => {
    if (!institutionId || !tciId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [profileRow, tcis, tciSnap] = await Promise.all([
        getTciGovernanceProfile({ institutionId, tciId }),
        listTciRegistry({ institutionId }),
        db.collection("tciRegistry").doc(tciId).get(),
      ]);
      setProfile(profileRow);
      const tciFromDoc = tciSnap.exists
        ? {
            id: tciSnap.id,
            ...(tciSnap.data() || {}),
            scope: tciSnap.data()?.scope || "global",
            institutionId: tciSnap.data()?.institutionId ?? null,
          }
        : null;
      const tci = tciFromDoc || tcis.find((row) => row.id === tciId);
      setTciRecord(tci || null);
      setTciName(tci?.name || tciId);
      reset({
        candidateNudgeThreshold: profileRow?.candidateNudgeThreshold ?? 2,
      });
    } catch (err) {
      setError(err?.message || "Failed to load governance profile.");
    } finally {
      setLoading(false);
    }
  }, [institutionId, tciId, reset]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const postureLabel = useMemo(() => {
    const match = AUTONOMY_POSTURES.find((row) => row.value === profile?.autonomyPosture);
    return match?.label || profile?.autonomyPosture;
  }, [profile?.autonomyPosture]);

  const cadenceLabel = useMemo(() => {
    const match = REVIEW_CADENCES.find((row) => row.value === profile?.reviewCadence);
    return match?.label || profile?.reviewCadence;
  }, [profile?.reviewCadence]);

  const cmrUsageModeMeta = useMemo(() => {
    const mode = profile?.cmrUsageMode;
    if (!mode) return null;
    return CMR_USAGE_MODES.find((row) => row.value === mode) || null;
  }, [profile?.cmrUsageMode]);

  const onSaveNudge = async ({ candidateNudgeThreshold }) => {
    if (!institutionId || !tciId) return;
    setSavingNudge(true);
    setError(null);
    setSuccess(null);
    try {
      await saveTciGovernanceProfile({
        institutionId,
        tciId,
        pass: "nudge-only",
        candidateNudgeThreshold: Number(candidateNudgeThreshold),
      });
      setSuccess("Nudge threshold updated.");
      await loadProfile();
    } catch (err) {
      setError(err?.message || "Failed to update nudge threshold.");
    } finally {
      setSavingNudge(false);
    }
  };

  const onSavePromptGraph = async (promptGraph) => {
    if (!institutionId || !tciId) return;
    setSavingGraph(true);
    setError(null);
    setSuccess(null);
    try {
      await saveTciGovernanceProfile({
        institutionId,
        tciId,
        pass: "prompt-graph",
        promptGraph,
      });
      setSuccess("Prompt graph saved.");
      await loadProfile();
    } catch (err) {
      setError(err?.message || "Failed to save prompt graph.");
      throw err;
    } finally {
      setSavingGraph(false);
    }
  };

  if (loading) return <Loading page />;

  const visitStartSection = isVisitStart ? (
    <VisitStartElementGovernanceSection
      institutionId={institutionId}
      profile={profile}
      canEdit={canEditVisitStartElements}
      allowedCategories={visitStartAllowedCategories}
      onSaved={loadProfile}
      onError={setError}
      onSuccess={setSuccess}
    />
  ) : null;

  if (!profile) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 900, mx: "auto" }}>
        <DashboardPageHeader
          title={tciName}
          subtitle="TCI governance profile"
        />
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
        {visitStartSection}
        <Alert severity="info">
          No governance profile exists for this TCI yet.{" "}
          <RouterLink to={`/dashboard/tci-governance/new?tciId=${encodeURIComponent(tciId)}`}>
            Start the interrogatory
          </RouterLink>
          .
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 900, mx: "auto" }}>
      <DashboardPageHeader
        title={tciName}
        subtitle="TCI governance profile"
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 1, mb: 3, flexWrap: "wrap" }}>
        <Chip
          label={GOVERNANCE_STATUS_LABELS[profile.status] || profile.status}
          color={profile.status === "active" ? "success" : "default"}
        />
        <Chip label={`Posture: ${postureLabel}`} variant="outlined" />
        <Chip label={`Cadence: ${cadenceLabel}`} variant="outlined" />
        <Chip
          label={`CMR: ${formatCmrUsageModeLabel(profile.cmrUsageMode)}`}
          variant="outlined"
          color={
            profile.cmrUsageMode && profile.cmrUsageMode !== "none"
              ? "primary"
              : "default"
          }
        />
      </Box>

      {canEditNudge && tciRecord?.scope === "institutional" ? (
        <TciSharingControl tci={tciRecord} onUpdated={loadProfile} />
      ) : null}

      {visitStartSection}

      <Typography variant="subtitle1" sx={{ mb: 1, mt: 2 }}>
        CMR (MasterChart) usage
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {cmrUsageModeMeta?.description ||
          (profile.cmrUsageMode
            ? formatCmrUsageModeLabel(profile.cmrUsageMode)
            : "Not specified in the interrogatory yet. Re-run the governance interrogatory to record whether this TCI reads or writes CMR data into the session record you review.")}
      </Typography>

      <Typography variant="subtitle1" sx={{ mb: 1, mt: 2 }}>
        Must do
      </Typography>
      <List dense sx={{ mb: 2 }}>
        {(profile.mustDo || []).map((item) => (
          <ListItem key={item} disableGutters>
            <ListItemText primary={item} />
          </ListItem>
        ))}
      </List>

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Must not do
      </Typography>
      <List dense sx={{ mb: 2 }}>
        {(profile.mustNotDo || []).map((item) => (
          <ListItem key={item} disableGutters>
            <ListItemText primary={item} />
          </ListItem>
        ))}
      </List>

      {profile.patientExperienceGoal && (
        <>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Patient experience goal
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {profile.patientExperienceGoal}
          </Typography>
        </>
      )}

      {(profile.feedbackSignals || []).length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
          {profile.feedbackSignals.map((signal) => (
            <Chip key={signal} label={signal} size="small" />
          ))}
        </Box>
      )}

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" sx={{ mb: 1 }}>
        Candidate nudge threshold
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        After this many submitted annotations without a candidate request, the Clinical Annotator receives a nudge.
      </Typography>

      {canEditNudge ? (
        <Box
          component="form"
          onSubmit={handleSubmit(onSaveNudge)}
          sx={{ display: "flex", gap: 2, alignItems: "flex-start", maxWidth: 360 }}
        >
          <TextField
            label="Nudge threshold"
            type="number"
            inputProps={{ min: 1, max: 50 }}
            {...register("candidateNudgeThreshold", { required: true, min: 1, max: 50 })}
          />
          <LoadingButton type="submit" variant="contained" loading={savingNudge}>
            Save
          </LoadingButton>
        </Box>
      ) : (
        <Typography variant="body1">{profile.candidateNudgeThreshold ?? 2}</Typography>
      )}

      {profile.status !== "active" && (
        <Alert severity="warning" sx={{ mt: 3 }}>
          Profile remains in configuring status until all interrogatory passes are complete and at
          least one active Clinical Annotator assignment exists for this TCI.
        </Alert>
      )}

      <PromptGraphEditor
        graph={profile.promptGraph || []}
        canEdit={canEditGraph}
        saving={savingGraph}
        onSave={onSavePromptGraph}
      />
    </Box>
  );
};

export default TciGovernanceView;
